/**
 * Contenido del modal «Total Células» del Dashboard: resumen + listado por día.
 */
import { Badge } from "@/components/ui/badge";
import { buildCellsOverview } from "@/lib/cellsOverview";

function modalityBadgeClass(modality) {
  const key = String(modality || "").trim().toLowerCase();
  if (key === "presencial") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (key === "en_linea") return "bg-sky-100 text-sky-800 border-sky-200";
  if (key === "hibrida") return "bg-violet-100 text-violet-800 border-violet-200";
  return "bg-gray-100 text-gray-600 border-gray-200";
}

/**
 * @param {{ cells: Array, members: Array, getMemberName: (id: string) => string }} props
 */
export function DashboardCellsOverview({ cells, members, getMemberName }) {
  const overview = buildCellsOverview({ cells, members, getMemberName });

  if (!overview.totalCells) {
    return <p className="text-gray-500 text-center py-8">No hay células registradas</p>;
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-blue-700">Total de células</p>
          <p className="mt-1 text-2xl font-bold text-blue-900">{overview.totalCells}</p>
        </div>
        <div className="rounded-lg border border-green-100 bg-green-50 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-green-700">Personas en células</p>
          <p className="mt-1 text-2xl font-bold text-green-900">{overview.totalPeople}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-600">Promedio por célula</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{overview.avgMembers}</p>
        </div>
      </div>

      {overview.networkBreakdown.length > 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Personas por red / tipo de célula</h3>
          <ul className="space-y-2">
            {overview.networkBreakdown.map((row) => (
              <li key={row.key} className="flex items-center justify-between gap-3 text-sm">
                <span className="font-medium text-gray-800">{row.label}</span>
                <span className="text-gray-600 tabular-nums">
                  {row.count} {row.count === 1 ? "persona" : "personas"} — {row.percent}%
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-gray-500">
            Cada persona se cuenta una sola vez según la célula en la que está registrada.
          </p>
        </div>
      ) : null}

      {overview.totalPeople > 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Distribución por sexo</h3>
          <ul className="space-y-2">
            {overview.sexBreakdown.map((row) => (
              <li key={row.key} className="flex items-center justify-between gap-3 text-sm">
                <span className="font-medium text-gray-800">{row.label}</span>
                <span className="text-gray-600 tabular-nums">
                  {row.count} {row.count === 1 ? "persona" : "personas"} — {row.percent}%
                </span>
              </li>
            ))}
            <li className="flex items-center justify-between gap-3 text-sm border-t border-gray-100 pt-2 mt-1">
              <span className="font-semibold text-gray-900">Total</span>
              <span className="font-semibold text-gray-900 tabular-nums">
                {overview.totalPeople} {overview.totalPeople === 1 ? "persona" : "personas"} — 100%
              </span>
            </li>
          </ul>
        </div>
      ) : null}

      <div className="max-h-[min(70vh,36rem)] overflow-y-auto space-y-5 pr-1">
        {overview.dayGroups.map((group) => (
          <section key={group.key} className="space-y-2">
            <div className="sticky top-0 z-10 flex items-center justify-between gap-2 rounded-md bg-gray-100 px-3 py-2 border border-gray-200">
              <h3 className="text-sm font-bold text-gray-800">{group.label}</h3>
              <span className="text-xs text-gray-600">
                {group.rows.length} {group.rows.length === 1 ? "célula" : "células"}
              </span>
            </div>
            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="w-full min-w-[920px] text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2.5 font-semibold text-gray-700">Célula</th>
                    <th className="text-left p-2.5 font-semibold text-gray-700">Red</th>
                    <th className="text-left p-2.5 font-semibold text-gray-700">Líder</th>
                    <th className="text-left p-2.5 font-semibold text-gray-700">Dirección</th>
                    <th className="text-left p-2.5 font-semibold text-gray-700">Zona</th>
                    <th className="text-left p-2.5 font-semibold text-gray-700">Modalidad</th>
                    <th className="text-left p-2.5 font-semibold text-gray-700">Hora</th>
                    <th className="text-left p-2.5 font-semibold text-gray-700">Miembros</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row) => (
                    <tr key={row.id || row.name} className="border-t border-gray-100 hover:bg-gray-50/80">
                      <td className="p-2.5 font-medium text-gray-900">{row.name}</td>
                      <td className="p-2.5 text-gray-700">{row.networkLabel}</td>
                      <td className="p-2.5 text-gray-700">{row.leaderName}</td>
                      <td className="p-2.5 text-gray-600">{row.address}</td>
                      <td className="p-2.5 text-gray-600">{row.location}</td>
                      <td className="p-2.5">
                        {row.meeting_modality ? (
                          <Badge
                            variant="outline"
                            className={`font-medium ${modalityBadgeClass(row.meeting_modality)}`}
                          >
                            {row.modalityLabel}
                          </Badge>
                        ) : (
                          <span className="text-gray-400 text-xs">Sin modalidad</span>
                        )}
                      </td>
                      <td className="p-2.5 text-gray-700 tabular-nums">{row.timeLabel}</td>
                      <td className="p-2.5">
                        <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                          {row.membersCount}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
